import { SocialPageClient } from "@/components/social/SocialPageClient";
import { useAuth } from "@/hooks/useAuth";
import { useMemo } from "react";
import { SocialPost, SocialStory } from "@/lib/types";
import { collection, query, where, orderBy, limit } from "firebase/firestore";
import { db } from "@/firebase/config";
import { sanitizeFirestoreData } from "@/lib/utils";
import { useCollection } from "@/firebase/firestore/use-collection";

export default function SocialPage() {
    const { user, loading: authLoading, jartinetData } = useAuth();

    // Solo los 150 más recientes (antes leía la colección entera en cada visita, [158]).
    const postsQuery = useMemo(
        () => query(collection(db, 'social_posts'), orderBy('createdAt', 'desc'), limit(150)),
        []
    );
    const { data: rawPosts } = useCollection<SocialPost>(postsQuery);

    const storiesQuery = useMemo(() => {
        const now = new Date();
        return query(collection(db, 'social_stories'), where('expiresAt', '>', now));
    }, []);
    const { data: rawStories } = useCollection<SocialStory>(storiesQuery);

    const allPosts = useMemo(
        () => (rawPosts || []).map(p => sanitizeFirestoreData(p) as SocialPost),
        [rawPosts]
    );

    const storiesByAuthor = useMemo(() => {
        return (rawStories || []).reduce((acc, story) => {
            const sanitized = sanitizeFirestoreData(story) as SocialStory;
            const key = `${sanitized.authorType}:${sanitized.authorId}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(sanitized);
            return acc;
        }, {} as Record<string, SocialStory[]>);
    }, [rawStories]);

    const allProfiles = useMemo(() => [
        ...(jartinetData.hermandades || []),
        ...(jartinetData.bandas || []),
        ...(jartinetData.organizaciones || []),
    ], [jartinetData]);

    const pinnedPostIds = useMemo(() => {
        const set = new Set<string>();
        allProfiles.forEach(profile => {
            if (profile.pinnedPosts) {
                Object.values(profile.pinnedPosts).forEach(id => { if (id) set.add(id); });
            }
        });
        return set;
    }, [allProfiles]);

    const mainFeedPosts = useMemo(
        () => allPosts.filter(p => !pinnedPostIds.has(p.id)),
        [allPosts, pinnedPostIds]
    );

    if (authLoading) return null;

    return (
        <div className="min-h-screen">
            <SocialPageClient
                initialHermandades={jartinetData.hermandades || []}
                initialBandas={jartinetData.bandas || []}
                initialOrganizaciones={jartinetData.organizaciones || []}
                initialPosts={mainFeedPosts}
                initialStories={storiesByAuthor}
                initialUserProfiles={jartinetData.userProfiles || {}}
                allPosts={allPosts}
                currentUser={user ? { uid: user.uid } : null}
            />
        </div>
    );
}
